import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAGiftRecipients() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const [ giftRecipients, setGiftRecipients ] = useState(
    powerOfAttorney.gift_recipients || [ { name: '', relationship: '' } ]
  );
  
  useScrollToTop();
  
  const handleBack = useCallback(() => {
    navigate('../gifts-to-others');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    const validRecipients = giftRecipients.filter(recipient => 
      recipient.name.trim() !== ''
    );
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      gift_recipients: validRecipients
    }));
    navigate('../granting-powers-complete');
  }, [ giftRecipients, setPowerOfAttorney, navigate ]);

  const addRecipient = useCallback(() => {
    setGiftRecipients(prev => [ ...prev, { name: '', relationship: '' } ]);
  }, []);

  const removeRecipient = useCallback((index: number) => {
    setGiftRecipients(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateRecipient = useCallback((index: number, field: string, value: string) => {
    setGiftRecipients(prev => prev.map((recipient, i) => 
      i === index ? { ...recipient, [field]: value } : recipient
    ));
  }, []);

  const isFormValid = giftRecipients.some(recipient => recipient.name.trim() !== '');
  
  return (
    <div className="new-poa-gift-recipients screen-container screen">
      <ScreenHeader
        title="Authorized Gift Recipients"
        description="List the people and organizations who can receive gifts from your agent"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Named Gift Recipients</h3>
        <p>
          List the specific individuals and organizations that your agent is 
          authorized to make gifts to on your behalf.
        </p>

        <div className="recipients-list">
          {giftRecipients.map((recipient, index) => (
            <div key={index} className="recipient-item">
              <div className="recipient-header">
                <h4>Recipient {index + 1}</h4>
                {giftRecipients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRecipient(index)}
                    className="remove-button"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="recipient-form">
                <div className="form-group">
                  <label htmlFor={`recipient-name-${index}`} className="form-label">
                    Name *
                  </label>
                  <input
                    id={`recipient-name-${index}`}
                    type="text"
                    value={recipient.name}
                    onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                    className="form-input"
                    placeholder="Full name or organization name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`recipient-relationship-${index}`} className="form-label">
                    Relationship (Optional)
                  </label>
                  <input
                    id={`recipient-relationship-${index}`}
                    type="text"
                    value={recipient.relationship}
                    onChange={(e) => updateRecipient(index, 'relationship', e.target.value)}
                    className="form-input"
                    placeholder="e.g., daughter, charity, friend"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addRecipient}
            className="add-recipient-button"
          >
            + Add Another Recipient
          </button>
        </div>

        <div className="important-note">
          <h4>Important:</h4>
          <p>
            Your agent will only be able to make gifts to the people and organizations 
            listed here. Make sure to include anyone you might want to receive gifts, 
            including family members, friends, and charitable organizations.
          </p>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
